import React, { useEffect, useRef } from 'react';
import { motion, useInView, useAnimation, useScroll, useTransform } from 'framer-motion';
import { FiGithub, FiLinkedin, FiMail, FiFacebook, FiInstagram, FiArrowDown } from 'react-icons/fi';
import Typewriter from 'typewriter-effect';
import HeroImage from "../assets/myprofile.png";
import { Link } from 'react-scroll'
const Home = () => {
  const controls = useAnimation();
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: false, margin: "-100px" });
  const { scrollYProgress } = useScroll();
  const scrollOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const scrollScale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);

  useEffect(() => {
    if (isInView) {
      controls.start("visible");
    }
  }, [controls, isInView]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const imageVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const socialVariants = {
    hover: (i) => ({
      y: -5,
      scale: 1.12,
      transition: {
        type: "spring",
        stiffness: 400,
        delay: i * 0.05
      }
    })
  };

  const backgroundCircleVariants = {
    initial: { scale: 0.8, opacity: 0 },
    animate: {
      scale: 1,
      opacity: 1,
      transition: {
        duration: 1.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <section
      id="home"
      className="min-h-screen flex items-center justify-center pt-20 lg:pt-0 bg-gray-50 dark:bg-gray-900 relative overflow-hidden"
      ref={containerRef}
    >
      <motion.div
        style={{ opacity: scrollOpacity, scale: scrollScale }}
        className="absolute inset-0 pointer-events-none"
      >
        <motion.div
          variants={backgroundCircleVariants}
          initial="initial"
          animate="animate"
          className="absolute top-0 right-0 w-96 h-96 lg:w-full lg:h-full bg-gradient-to-r from-indigo-500/10 to-purple-500/5 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3"
        />
        <motion.div
          variants={backgroundCircleVariants}
          initial="initial"
          animate="animate"
          className="absolute bottom-0 left-0 w-96 h-96 lg:w-full lg:h-full bg-gradient-to-r from-primary/10 to-secondary/5 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3"
        />
      </motion.div>

      <div className="max-w-6xl w-full px-4 sm:px-6 lg:px-8 py-10 lg:py-20 z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={controls}
          className="grid lg:grid-cols-2 gap-12 items-center"
        >
          {/* Left Column - Text Content */}
          <div className="text-left order-2 lg:order-1">
            <motion.div
              variants={itemVariants}
              className="inline-block font-mono px-4 py-1.5 mb-6 bg-primary/10 text-indigo-800 dark:text-yellow-300 font-medium rounded-full text-sm tracking-wide"
            >
              Welcome to my  portfolio
            </motion.div>

            <motion.h1
              variants={itemVariants}
              className="text-4xl font-times md:text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-tight"
            >
              Hi, I am{" "}
              <span className="text-transparent text-nowrap bg-clip-text bg-gradient-to-r from-primary via-indigo-500 to-secondary">
                Jivan Dahal
              </span>
            </motion.h1>

            <motion.div
              variants={itemVariants}
              className="text-xl font-montserrat md:text-2xl lg:text-3xl text-gray-700 dark:text-gray-200 font-semibold mb-6 h-16"
            >
              <Typewriter
                options={{
                  strings: [
                    "Full Stack Developer",
                    "UI/UX Designer",
                    "Tech Enthusiast",
                    "Problem Solver"
                  ],
                  autoStart: true,
                  loop: true,
                  deleteSpeed: 40,
                  delay: 60
                }}
              />
            </motion.div>

            <motion.p
              variants={itemVariants}
              className="text-lg font-robotoMono text-gray-600 font-medium text-justify dark:text-gray-300 mb-8 leading-relaxed max-w-lg"
            >
              "I focus on delivering thoughtful, user-focused web solutions that align with clients’ goals, with strong attention to performance, usability, and clean design.
              I continuously explore new ideas to create easy-to-use and impactful digital experiences."
            </motion.p>

            <motion.div
              variants={itemVariants}
              className="flex flex-wrap gap-4 mb-12"
            >
              <Link
                to="projects"      // This should match the id of your target section
                spy={true}
                smooth={true}
                offset={-64}      // Adjust depending on your header height
                duration={500}    // Duration of the scroll (in ms)
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="px-8 py-3.5 font-robotoMono bg-gradient-to-r from-primary to-secondary text-white rounded-lg font-medium shadow-lg hover:shadow-primary/20 transition-all duration-300 flex items-center gap-2"
                >
                  View Projects <FiArrowDown className="inline-block ml-1" />
                </motion.button>
              </Link>


            </motion.div>

            <motion.div
              variants={itemVariants}
              className="flex gap-6"
            >
              {[
                {
                  icon: FiGithub,
                  href: "https://github.com/Jivan-Dahal",
                  label: "GitHub",
                  color: "text-[#181717] hover:text-[#0f0f0f]" // GitHub Black
                },
                {
                  icon: FiLinkedin,
                  href: "https://www.linkedin.com/in/jivan-dahal-611372334/",
                  label: "LinkedIn",
                  color: "text-[#0077B5] hover:text-[#005582]" // LinkedIn Blue
                },
                {
                  icon: FiMail,
                  href: "mailto:dahaljivan02@gmail.com",
                  label: "Email",
                  color: "text-[#D44638] hover:text-[#a3332a]" // Gmail Red
                },
                {
                  icon: FiFacebook,
                  href: "https://www.facebook.com/jivandahal2",
                  label: "Facebook",
                  color: "text-[#1877F2] hover:text-[#0f5dc8]" // Facebook Blue
                },
                {
                  icon: FiInstagram,
                  href: "https://www.instagram.com/jivan_dahal__",
                  label: "Instagram",
                  color: "text-[#E4405F] hover:text-[#c32e4a]" // Instagram Pink
                }
                
              ].map((social, index) => (
                <motion.a
                  key={index}
                  custom={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`  ${social.color} transition-colors duration-300 p-2 bg-white dark:bg-gray-200  rounded-lg shadow-md`}
                  variants={socialVariants}
                  whileHover="hover"
                  aria-label={social.label}
                >
                  <social.icon className="w-6 h-6" />
                </motion.a>
              ))}
            </motion.div>
          </div>

          {/* Right Column - Image */}
          <motion.div
            className="relative order-1  lg:order-2 flex justify-center lg:justify-end"
            variants={itemVariants}
          >
            <motion.div
              className="relative"
              variants={imageVariants}
            >
              <motion.div
                className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-full blur-2xl"
                animate={{
                  scale: [1, 1.05, 1],
                  opacity: [0.5, 0.6, 0.5]
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              />
              <motion.div
                className="relative w-60 h-60 md:w-72 md:h-72 lg:w-[28rem] lg:h-[28rem] rounded-full overflow-hidden ring-4 ring-white dark:ring-green-800 shadow-xl"
                whileHover={{ scale: 1.03 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <img
                  src={HeroImage}
                  alt="Jivan Dahal"
                  className="w-full h-full object-cover"
                />
              </motion.div>

              {/* Tech badges around the image */}
              {[
                { label: "React", top: "10%", left: "5%", delay: 0.3 },
                { label: "Laravel", top: "70%", left: "0%", delay: 0.5 },
                { label: "Django", top: "20%", right: "-5%", delay: 0.4 },
                { label: "UI/UX", bottom: "15%", right: "0%", delay: 0.6 }
              ].map((badge, index) => (
                <motion.div
                  key={index}
                  className="absolute bg-white font-robotoMono dark:bg-red-700 px-3 py-1.5 rounded-full text-xs md:text-sm font-medium shadow-lg text-gray-800 dark:text-white"
                  style={{
                    top: badge.top || "auto",
                    left: badge.left || "auto",
                    right: badge.right || "auto",
                    bottom: badge.bottom || "auto"
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    transition: { delay: badge.delay, duration: 0.6 }
                  }}
                >
                  {badge.label}
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </motion.div>
      </div>


    </section>
  );
};

export default Home;